import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { all_routes } from "../../router/all_routes";
import {
  getUpdateSettingAPI,
  updateSettingAPI,
} from "../../../core/data/redux/settingsSlice";
import { useDispatch, useSelector } from "react-redux";
import Toasts from "../../../core/common/toast";
import i18n, { t, setLanguage as setAppLanguage, getLanguage } from "../../../core/i18n";
import Switch from "@mui/material/Switch";
const themeColors: Record<"Cyan" | "Blue" | "Green" | "Orange", string> = {
  Cyan: "#00BCD4",
  Blue: "#2196F3",
  Green: "#4CAF50",
  Orange: "#FF5722",
};

type ThemeName = keyof typeof themeColors;

const SettingsPage = () => {
  const dispatch = useDispatch();
  const settingsState = useSelector((state: any) => state.settingsState);

  type Locale = "en" | "hi";
  const [language, setLanguage] = useState<Locale>(getLanguage() as Locale);
  const [dmrcEnabled, setDmrcEnabled] = useState<boolean>(false);
  const [darkMode, setDarkMode] = useState<boolean>(false);

  // Fetch settings on mount
  useEffect(() => {
    dispatch(getUpdateSettingAPI({}));
  }, [dispatch]);

  // ✅ Update state ONLY when new value comes
  useEffect(() => {
    const settings = settingsState?.data?.data;

    if (settings?.dmrc_notification?.value !== undefined) {
      setDmrcEnabled(settings.dmrc_notification.value);
    }

    if (settings?.dark_mode?.value !== undefined) {
      setDarkMode(settings.dark_mode.value);
    }
  }, [
    settingsState?.data?.data?.dmrc_notification?.value,
    settingsState?.data?.data?.dark_mode?.value,
  ]);

  // Apply dark theme attribute to the document so CSS rules using [data-theme="dark"] become active
  useEffect(() => {
    try {
      const root = document.documentElement;
      const body = document.body;

      if (darkMode) {
        root.setAttribute("data-theme", "dark");
        root.setAttribute("data-theme-mode", "dark");
        body.setAttribute("data-theme", "dark");
      } else {
        root.removeAttribute("data-theme");
        root.removeAttribute("data-theme-mode");
        body.removeAttribute("data-theme");
      }
    } catch (e) {
      // ignore in non-browser environments
    }
  }, [darkMode]);

  const handleToggle = async (key: string, currentVal: boolean) => {
    const newValue = !currentVal;

    // Optimistically update UI
    if (key === "dmrc_notification") setDmrcEnabled(newValue);
    if (key === "dark_mode") setDarkMode(newValue);

    try {
      await dispatch(
        updateSettingAPI({ setting_key: key, setting_value: newValue })
      ).unwrap();
    } catch (err) {
      // Rollback on failure
      if (key === "dmrc_notification") setDmrcEnabled(!newValue);
      if (key === "dark_mode") setDarkMode(!newValue);
    }
  };

  const handleSettingChange = (key: string, value: string) => {
    dispatch(updateSettingAPI({ setting_key: key, setting_value: value }));
  };

  const [colorTheme, setColorTheme] = useState<ThemeName>("Green");

  return (
    <div>
      <Toasts
        show={settingsState.loading === "rejected"}
        message={settingsState.error}
        title={"Failed"}
        type={"danger"}
      />
      <Toasts
        show={
          settingsState.loading === "fulfilled" &&
          settingsState.action !== "list"
        }
        message={settingsState?.data?.message}
        title={"Success"}
        type={"success"}
      />

      <div className="page-wrapper">
        <div className="content">
          <div className="d-md-flex d-block align-items-center justify-content-between mb-3">
            <div className="my-auto mb-2">
              <h3 className="page-title mb-1">Attendance Regularize</h3>
              <nav>
                <ol className="breadcrumb mb-0">
                  <li className="breadcrumb-item">
                    <Link to={all_routes.adminDashboard}>Dashboard</Link>
                  </li>
                  <li className="breadcrumb-item">
                    <Link to="#">Employees</Link>
                  </li>
                  <li className="breadcrumb-item active" aria-current="page">
                    Attendance Regularize
                  </li>
                </ol>
              </nav>
            </div>
          </div>

          <div className="max-w-md mx-auto p-4 space-y-4">
            {/* Attendance Card */}
            <div className="bg-white shadow-md rounded-lg p-4 flex justify-between items-center">
              <div>
                <p className="text-lg font-medium">{t("attendance_registration")}</p>
                <p className="text-green-600 text-sm">{t("approved")}</p>
              </div>
              {/* <div className="flex flex-col items-center">
                <div className="w-10 h-10 rounded-full bg-gray-300 mb-1" />
                <button className="text-red-500 text-sm">Edit Image</button>
              </div> */}
            </div>

            <div className="bg-white shadow-md rounded-lg p-4 flex justify-between items-center">
              <span className="font-medium">{t("dmrc_notification")}</span>
              <Switch
                checked={dmrcEnabled}
                onChange={() => handleToggle("dmrc_notification", dmrcEnabled)}
              />
            </div>

            {/* Dark Mode Toggle */}
            <div className="bg-white shadow-md rounded-lg p-4 flex justify-between items-center">
              <span className="font-medium">{t("dark_mode")}</span>
              <Switch
                checked={darkMode}
                onChange={() => handleToggle("dark_mode", darkMode)}
              />
            </div>

            {/* Theme Selector */}
            {/* ✅ Fixed Theme Selector */}
            <div className="bg-white shadow-md rounded-lg p-4 flex items-center justify-between">
              <div className="flex items-center gap-4">
                <span className="font-medium">{t("choose_color_theme")}</span>

                <div className="flex gap-4">
                  {Object.entries(themeColors).map(([key, hex]) => (
                    <i
                      key={key}
                      className="ti ti-edit text-xl cursor-pointer transition-transform"
                      title={key}
                      style={{
                        color: hex,
                        transform: colorTheme === key ? "scale(1.3)" : "scale(1)",
                      }}
                      onClick={() => {
                        setColorTheme(key as ThemeName);
                        handleSettingChange("Theme", key);
                      }}
                    />
                  ))}
                </div>
              </div>
            </div>

            {/* Language Selector */}
            <div className="bg-white shadow-md rounded-lg p-4">
              <p className="font-medium mb-2">{t("choose_language")}</p>
              <div className="mb-2">{t(language === "en" ? "english" : "hindi")}</div>
              <select
                className="w-full border border-gray-300 rounded px-3 py-2"
                value={language}
                onChange={(e) => {
                  const val = e.target.value as Locale;
                  setLanguage(val);
                  setAppLanguage(val);
                  // persist in server settings as a human-readable label for backwards compatibility
                  const labelMap: Record<Locale, string> = { en: "English", hi: "Hindi" };
                  handleSettingChange("Language", labelMap[val]);
                }}
              >
                <option value="en">{t("english")}</option>
                <option value="hi">{t("hindi")}</option>
              </select>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SettingsPage;
